import { useEffect, useMemo, useState } from "react";
import { getPedidos, updateEstadoPedido, confirmarPagoPedido } from "../../../services/admin/pedidos_admin";
import OrderViewModal from "./OrderViewModal";
import { IconView, IconTimeline, IconDocs } from "../../../icons/Icons";
import OrderTimelineModal from "./OrderTimelineModal";
import MenuAcciones from "../../../components/admin/MenuAcciones";
import MultiSelect from "../../../components/admin/MultiSelect";
import {
  ESTADOS,
  estadoDe,
  formatoUSD,
  siguientePaso,
  textoDePago,
} from "./estadosPedido";
import { hasPerm } from "../../../utils/hasPerm";
import "./OrdersPage.css";
import OrderDocsModal from "./OrderDocsModalAdmin";

const DEFAULT_PAGE_SIZE = 25;

// Sin unidad a proposito: hoy ninguna linea de pedido tiene unidad_id
// cargada, asi que poner "lb" seria inventarlo.
const formatoPeso = (valor) => {
  const n = Number(valor);
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

function OrdersPage() {
  const [pedidos, setPedidos] = useState([]);
  const [search, setSearch] = useState("");

  // Los tres filtros son listas de valores elegidos. Vacio = no filtra, que
  // no es lo mismo que "ninguno".
  const [estadoIds, setEstadoIds] = useState([]);
  const [pagoIds, setPagoIds] = useState([]);
  const [envioIds, setEnvioIds] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showTimeline, setShowTimeline] = useState(false);
  const [selectedTimelineOrder, setSelectedTimelineOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [page, setPage] = useState(1);
  const [pageCursors, setPageCursors] = useState({ 1: null });
  const [nextCursorByPage, setNextCursorByPage] = useState({});
  const [showDocs, setShowDocs] = useState(false);
  const [alert, setAlert] = useState(null);
  const [confirmPaymentOrder, setConfirmPaymentOrder] = useState(null);

  useEffect(() => {
    cargarPagina(1);
  }, []);

  useEffect(() => {
    setPage(1);
    setPageCursors({ 1: null });
    setNextCursorByPage({});
    cargarPagina(1);

  }, [pageSize]);

  const cargarPagina = async (pageNumber) => {
    try {
      setLoading(true);

      const cursor = pageCursors[pageNumber] ?? null;
      const resp = await getPedidos({ limit: pageSize, cursor });

      const rows = Array.isArray(resp?.data) ? resp.data : [];
      const nextCursor = resp?.nextCursor || null;

      setPedidos(rows);

      setNextCursorByPage((prev) => ({ ...prev, [pageNumber]: nextCursor }));

      if (nextCursor) {
        setPageCursors((prev) => {
          if (prev[pageNumber + 1] != null) return prev;
          return { ...prev, [pageNumber + 1]: nextCursor };
        });
      }

      setPage(pageNumber);
    } catch (error) {
      console.error("Error cargando pedidos:", error);
    } finally {
      setLoading(false);
    }
  };

  const cambiarEstado = async (pedido_id, nuevo) => {
    try {
      setUpdating(true);
      await updateEstadoPedido(pedido_id, nuevo);
      await cargarPagina(page);
    } catch (error) {
      console.error("Error cambiando estado:", error);
    } finally {
      setUpdating(false);
    }
  };

  const marcarPago = async (pedido_id) => {
    // esta función ahora solo se encarga de ejecutar la acción
    try {
      setUpdating(true);
      await confirmarPagoPedido(pedido_id);
      await cargarPagina(page);
      setAlert({
        type: "success",
        message: "The sales order has been marked as paid and the stock has been updated.",
      });
    } catch (error) {
      console.error("Error confirming payment:", error);
      const msg =
        error?.response?.data?.message ||
        error?.message ||
        "Error confirming payment";
      setAlert({
        type: "error",
        message: msg,
      });
    } finally {
      setUpdating(false);
    }
  };

  /**
   * Los transportistas que aparecen en su filtro.
   *
   * Salen de las ordenes cargadas y no de un catalogo: filtrar por un
   * transportista que ninguna orden de la pagina usa no devolveria nada.
   *
   * Los estados y las formas de pago, en cambio, son catalogo fijo: si en
   * las 25 filas visibles no hay ninguna cancelada, "Canceled" igual tiene
   * que poder elegirse.
   */
  const enviosDisponibles = useMemo(() => {
    const vistos = new Map();
    pedidos.forEach((p) => {
      const nombre = p.metodo_envio || p.nombre_metodo_envio || "";
      // La clave es el nombre mismo: la lista no trae el id del metodo.
      if (!vistos.has(nombre)) vistos.set(nombre, nombre);
    });
    return Array.from(vistos.keys()).sort((a, b) =>
      a === "" ? 1 : b === "" ? -1 : a.localeCompare(b)
    );
  }, [pedidos]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();

    // Sin seleccion el filtro no filtra. Con varios alcanza con que la orden
    // coincida con uno: son alternativas, no condiciones que se sumen.
    const coincide = (ids, valor) =>
      ids.length === 0 || ids.map(String).includes(String(valor ?? ""));

    return pedidos.filter((p) => {
      const pedidoId = String(p.pedido_id ?? "").toLowerCase();
      const clienteId = String(p.cliente_id ?? "").toLowerCase();
      const nopo = String(p.no_po ?? "").toLowerCase();
      const matchSearch = !q ? true : `${pedidoId} ${clienteId} ${nopo}`.includes(q);

      return (
        matchSearch &&
        coincide(estadoIds, p.estado) &&
        coincide(pagoIds, textoDePago(p)) &&
        coincide(envioIds, p.metodo_envio || p.nombre_metodo_envio || "")
      );
    });
  }, [pedidos, search, estadoIds, pagoIds, envioIds]);

  const hasPrev = page > 1;
  const hasNext = Boolean(nextCursorByPage[page]);

  const totalPages = useMemo(() => {
    const keys = Object.keys(pageCursors).map(Number);
    return keys.length ? Math.max(...keys) : 1;
  }, [pageCursors]);

  const nextPage = () => hasNext && cargarPagina(page + 1);
  const prevPage = () => hasPrev && cargarPagina(page - 1);

  return (
    <div className="omx-orders-scope">
      {updating && (
        <div className="overlay">
          <div className="overlay-card">
            <div className="spinner" />
            <div className="overlay-title">Updating sales order...</div>
            <div className="overlay-sub">Please wait</div>
          </div>
        </div>
      )}

      <div className="header">
        <h2 className="title">Sales Orders</h2>

        {/* Los cinco controles en un renglon. Solo bajan cuando la
            pantalla se angosta y ya no entran. */}
        <div className="top-bar">
          <div className="barra-buscar">
            <input
              type="text"
              placeholder="Search SO #, customer code, PO#"
              className="search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div className="barra-filtros">
            <span className="barra-filtros__et">Filter</span>

            <MultiSelect
              valores={estadoIds}
              onChange={setEstadoIds}
              options={ESTADOS.map((e) => ({ value: e.codigo, label: e.texto }))}
              textoVacio="All statuses"
              nombre="statuses"
              buscarPlaceholder="Search status..."
            />

            <MultiSelect
              valores={pagoIds}
              onChange={setPagoIds}
              options={[
                { value: "Paid", label: "Paid" },
                { value: "Not paid", label: "Not paid" },
              ]}
              textoVacio="All payments"
              nombre="payment states"
              buscarPlaceholder="Search payment..."
            />

            <MultiSelect
              valores={envioIds}
              onChange={setEnvioIds}
              options={enviosDisponibles.map((n) => ({
                value: n,
                label: n || "— Not set —",
              }))}
              textoVacio="All carriers"
              nombre="carriers"
              buscarPlaceholder="Search carrier..."
              disabled={enviosDisponibles.length === 0}
            />
          </div>
        </div>
      </div>

      <div className="table-card">
        <table className="modern-table">
          <thead>
            <tr>
              <th className="th-num">SO #</th>
              <th>PO#</th>
              <th>Customer</th>
              <th>Shipping</th>
              <th className="th-cifra">Total</th>
              <th className="th-cifra">Weight</th>
              <th>Created</th>
              <th>Status</th>
              <th>Payment</th>
              <th className="th-actions">Actions</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan="9" className="no-data">Loading...</td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan="9" className="no-data">No sales orders found</td>
              </tr>
            ) : (
              filtered.map((p) => {
                const est = estadoDe(p);
                const payText = textoDePago(p);
                const sinPagar = payText === "Not paid";
                const envio = p.metodo_envio || p.nombre_metodo_envio || "";
                const siguiente = siguientePaso(p);

                return (
                  <tr key={p.pedido_id}>
                    <td className="td-num">{p.pedido_id}</td>

                    {/* Sin PO va una raya y no una celda en blanco: en
                        blanco no se sabe si falta el dato o falta cargar. */}
                    <td>{p.no_po || <span className="vacio">—</span>}</td>

                    <td>
                      <div className="customer-name">
                        {p.cliente_nombre || `Customer #${p.cliente_id}`}
                      </div>
                      <div className="customer-code">#{p.cliente_id}</div>
                    </td>

                    {/* Una linea y con el texto completo en el tooltip:
                        "Store will select the best shipping method" ocupaba
                        cuatro renglones y estiraba toda la fila. */}
                    <td className="td-envio" title={envio}>
                      {envio || <span className="vacio">—</span>}
                    </td>

                    <td className="td-cifra td-total">
                      {formatoUSD(p.total_factura)}
                    </td>

                    <td className="td-cifra">{formatoPeso(p.peso_total)}</td>

                    <td className="dates">
                      {String(p.fecha_creado || "").split("T")[0]}
                    </td>

                    <td>
                      <span className={`status ${est.clase}`}>{est.texto}</span>
                    </td>

                    <td>
                      <span className={`status ${pagoClass(payText)}`}>
                        {payText}
                      </span>
                    </td>

                    <td>
                      {/* Lo que se usa todos los dias queda a un clic: ver,
                          historial, documentos y el paso siguiente de la
                          orden. Lo que no se deshace vive en el menu. */}
                      <div className="actions-wrapper">
                        {hasPerm("View orders") && (
                          <span
                            className="action-icon view"
                            title="View sales order"
                            onClick={() => {
                              setSelectedOrder(p.pedido_id);
                              setShowModal(true);
                            }}
                          >
                            {IconView}
                          </span>
                        )}

                        {hasPerm("Timeline orders") && (
                          <span
                            className="action-icon view"
                            title="Timeline"
                            onClick={() => {
                              setSelectedTimelineOrder(p.pedido_id);
                              setShowTimeline(true);
                            }}
                          >
                            {IconTimeline}
                          </span>
                        )}

                        {hasPerm("Upload order docs") && (
                          <span
                            className="action-icon view"
                            title="Documents"
                            onClick={() => {
                              setSelectedOrder(p.pedido_id);
                              setShowDocs(true);
                            }}
                          >
                            {IconDocs}
                          </span>
                        )}

                        {siguiente && hasPerm("Move orders") ? (
                          <button
                            className="btn-state btn-primary is-solid btn-paso"
                            disabled={updating}
                            onClick={() =>
                              cambiarEstado(p.pedido_id, siguiente.codigo)
                            }
                            title={siguiente.ayuda}
                          >
                            {siguiente.texto}
                          </button>
                        ) : (
                          <span className="btn-paso-hueco" aria-hidden="true" />
                        )}

                        <MenuAcciones
                          opciones={[
                            sinPagar &&
                              hasPerm("Move orders") && {
                                clave: "pago",
                                texto: "Mark as paid",
                                desactivada: updating,
                                alElegir: () =>
                                  setConfirmPaymentOrder(p.pedido_id),
                              },
                            Number(p.estado) !== 3 &&
                              Number(p.estado) !== 4 &&
                              hasPerm("Cancel orders") && {
                                clave: "cancelar",
                                texto: "Cancel sales order",
                                peligrosa: true,
                                desactivada: updating,
                                alElegir: () => cambiarEstado(p.pedido_id, 4),
                              },
                          ]}
                        />
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {!loading && (
        <div className="pagination">
          {/* Hueco del mismo peso que el total de la derecha, para que el
              grupo del centro quede centrado de verdad. */}
          <span className="pag-lado" aria-hidden="true" />

          <div className="pag-nav">
            <button onClick={prevPage} disabled={!hasPrev}>
              Prev
            </button>

            <span>
              Page {page} of {totalPages}
            </span>

            <button onClick={nextPage} disabled={!hasNext}>
              Next
            </button>

            {/* Junto a Prev/Next y no entre los filtros: cuantas filas se
                ven es parte de moverse por las paginas, no de filtrar. */}
            <div className="pag-tamano">
              <label htmlFor="pag-tamano">Rows</label>
              <select
                id="pag-tamano"
                className="page-size-select"
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
              >
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          </div>

          <span className="pag-total">
            {filtered.length}{" "}
            {filtered.length === 1 ? "sales order" : "sales orders"}
            {filtered.length !== pedidos.length
              ? ` of ${pedidos.length}`
              : ""}
          </span>
        </div>
      )}

      {alert && (
        <div className={`orders-alert orders-alert-${alert.type}`}>
          <div className="orders-alert-content">
            <span className="orders-alert-title">
              {alert.type === "success" ? "Success" : "Attention"}
            </span>
            <span className="orders-alert-message">{alert.message}</span>
          </div>
          <button
            className="orders-alert-close"
            type="button"
            onClick={() => setAlert(null)}
          >
            ×
          </button>
        </div>
      )}

      {confirmPaymentOrder && (
        <div className="overlay confirm-overlay">
          <div className="overlay-card confirm-card">
            <div className="overlay-title">Mark as Paid</div>
            <div className="overlay-sub">
              Are you sure you want to mark this sales order as paid and update the stock?
            </div>
            <div className="confirm-actions">
              <button
                type="button"
                className="btn-state btn-danger"
                onClick={() => setConfirmPaymentOrder(null)}
                disabled={updating}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-state btn-primary is-solid"
                onClick={() => {
                  const id = confirmPaymentOrder;
                  setConfirmPaymentOrder(null);
                  marcarPago(id);
                }}
                disabled={updating}
              >
                Yes, mark as paid
              </button>
            </div>
          </div>
        </div>
      )}

      {showModal && (
        <OrderViewModal
          pedidoId={selectedOrder}
          onClose={() => setShowModal(false)}
        />
      )}

      {showTimeline && (
        <OrderTimelineModal
          pedidoId={selectedTimelineOrder}
          onClose={() => setShowTimeline(false)}
        />
      )}

        {showDocs && (
    <OrderDocsModal
      pedidoId={selectedOrder}
      onClose={() => setShowDocs(false)}
    />
  )}
    </div>
  );
}

export default OrdersPage;

const pagoClass = (paymentText) => {
  const v = String(paymentText || "").toLowerCase();
  if (v === "paid") return "paid";
  return "pending";
};
